import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";

import fpJobChangeTimeInfoService from "@plannel/services/fp/fp-job-change-time-info-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — PlanResult 파일럿에서 실제 확인된 값(9)을
// 그대로 재사용한다(fp-report-screen-guide.md §A-3c). old JobChangeTimeInfo.jsx의 로컬 fallback
// (14, `let fpSettings = { searchZone: 14 }`)이 아니라 그 실제 DB 값을 이식한다.
// TODO(MIG-T2): [jobchangetimeinfo/JobChangeTimeInfo] 조회조건 세팅 3종(버킷 목록/검색필드 표시/
//   검색구간)의 테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드는 resourceCdNm 하나뿐이다(loadData/
// loadChartData의 getValues 기준). 백엔드(JcTimeInfoQueryRepository#makeWhereClause)도
// resourceCdNm만 소비하고 stageCdNm 조건은 없다.
const SEARCH_FIELD_LABELS = { resourceCdNm: "FP_RESOURCE" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// old 백엔드 analysisIndicator 2종(JobChangeTimeInfoService의 TIMES/FP_TIME_MINUTES 상수) 고정
// 색상 — ResourceUtilization/MaterialPsi와 같은 이유로 화면 고정 배열로 선언한다(백엔드가 항상
// 이 2개만 내려주므로 PlanResult의 itemCd처럼 클릭 시점에 동적으로 시리즈를 조립할 필요 없음).
// TIMES(작업교체 횟수)는 old처럼 선(line)+우측축, FP_TIME_MINUTES(작업교체 시간, 분)는 막대(bar)+
// 좌측축 — old JobChangeTimeInfoChart.jsx의 rightYAxis 듀얼축 구성과 동일(MaterialPsi.js의
// INDICATOR_CONFIG 듀얼축 패턴을 그대로 재사용).
const INDICATOR_ORDER = ["FP_TIME_MINUTES", "TIMES"];
const INDICATOR_CONFIG = {
  FP_TIME_MINUTES: { type: "bar", orientation: "left", color: "#FFC950" },
  TIMES: { type: "line", orientation: "right", color: "#4472C4" }
};

// 트리(행 그룹) 계층 — 공장 > 자원(leaf는 analysisIndicator, 2개 고정 지표). old 컬럼정의
// (plantGroup/resourceGroup 모두 mergeRule)를 근거로 Row Grouping을 선택했다(Tree Data가 아님,
// fp-report-screen-guide.md §A-6b). 백엔드 DTO(JcTimeInfoCrosstabDto/QActivityEntity)는
// stageCd/stageNm도 내려주지만 old 화면은 스테이지 레벨을 노출하지 않았다(2026-08-11 한 차례
// ResourceUtilization과 맞춰 3단으로 노출했다가, old 화면 그대로 유지하라는 사람 판단으로 되돌림
// — 백엔드가 앞서 있어도 화면 범위를 확장할지는 항상 사람 확인 대상).
const GROUP_FIELDS = ["plantCd", "plantNm", "resourceCd", "resourceNm"];

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_CD_COL_ID = "resourceCdGrp";
const RESOURCE_NM_COL_ID = `RESOURCE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const RESOURCE_NM_HIDE_KEY = "resourceNmColsHide";

const JobChangeTimeInfo = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // resourceCdNm — Report 공통 정책(§A-9b)대로 조회 버튼이 없다. 자유 텍스트는
  // SearchOptsGroupRetrieve(TextFieldSearch 기반) 안의 Enter/검색아이콘으로만 커밋한다.
  const resourceCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  // 응답 순서 뒤바뀜 방지 — 조회(그리드)와 차트는 서로 다른 요청이라 가드도 2개(§0-8).
  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveJobChangeTimeInfo(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      // 조회 전용 화면 — 공용 DefaultGridSetting의 defaultColDef.editable:true를 끈다.
      defaultColDef: { ...base.defaultColDef, editable: false },
      // read-only 화면이라 DataState.getRowStyles(편집 상태 표시용)는 의미가 없다
      // (ResourceUtilization/MaterialPsi와 동일 판단).
      getRowStyle: () => null,
      // 행 그룹(트리) 3종 세트 — PlanResult/ResourceUtilization/MaterialPsi와 동일한 조합.
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(plantCd/plantNm/resourceCd/resourceNm/
  // analysisIndicator/sum)과 1:1(위 GROUP_FIELDS 주석 참고). 공장/자원은 행 그룹(트리),
  // analysisIndicator는 트리의 leaf(실제 데이터 행을 구분하는 값), sum은 평범한 숫자 컬럼이다.
  //
  // TODO(MIG-T2): [jobchangetimeinfo/JobChangeTimeInfo] resourceCd==='SUM' 합계 행 강조
  // (styleCallback의 'summary-col') 미이관.
  //   old: grid.getValue(itemIndex,'resourceCd') === 'SUM'이면 합계 행에 강조 스타일을 추가
  //   사유: 새 백엔드(JobChangeTimeInfoService#getJcTimeInfo)는 플랜트/스테이지/자원 그룹별
  //        FP_TIME_MINUTES·TIMES 2행만 만들고 resourceCd='SUM' 합성 합계 행 자체를 만들지 않는다
  //        (서비스 코드 확인) — backend-gaps.md #7 등재, 프론트에서 임의로 재구현하지 않음
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY),
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, RESOURCE_NM_HIDE_KEY),
      cdColId: RESOURCE_CD_COL_ID, cdField: "resourceCd", cdHeader: "FP_RESOURCE_CD",
      nmColId: RESOURCE_NM_COL_ID, nmField: "resourceNm", nmHeader: "FP_RESOURCE_NM",
      hideKey: RESOURCE_NM_HIDE_KEY, width: 150
    }),

    // leaf(분석 지표) — old displayCallback가 항상 transLangKey(value)로 번역해서 보여준다.
    {
      headerName: "FP_ANALYSIS_INDICATOR", field: "analysisIndicator", colId: "analysisIndicator", width: 150,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"],
      valueFormatter: (params) => t(params.value)
    },

    // sum — old gridItems의 numeric 컬럼(headerName "SUM", GridUtils가 i18n.exists("SUM")로
    // 자동 번역). analysisIndicator별 전체 버킷 합계(JobChangeTimeInfoService의 sumJcTime/sumJcCount).
    { headerName: "SUM", field: "sum", colId: "sum", width: 100, pinned: "left", lockPinned: true, type: ["numberColumn", "rightAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // 이 화면의 백엔드(JobChangeTimeInfoService#getJcTimeInfo)는 시간 버킷이면 빈 결과를
  // 반환한다(`Bucket.NULL.equals(bucket) || bucket.isHourBucket()`) — ResProdPlan/
  // ResourceUtilization과 같은 이유로 이 화면도 BUCKET_CASE.WITHOUT_HOUR를 써야 한다
  // (fp-report-screen-guide.md §A-3 배정표에 이 화면을 추가함). 일버킷 자체가 안 나오는
  // 화면이라 요일 코드 분기는 실제로 안 타지만, common/grid.js의 buildDayBucketPivotColumnDefs를
  // 그대로 써도 동작은 동일하다(다른 Report 화면과 일관성 유지).
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t);

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      resourceCd: row.resourceCd, resourceNm: row.resourceNm,
      analysisIndicator: row.analysisIndicator, sum: row.sum
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      resourceCdNm: resourceCdNmRef.current,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — 공장/자원 2단이라 originLoc=공장, item 슬롯을 자원으로 빌려 쓴다
  // (§A-7 슬롯 매핑 참고).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      itemCd: target.resourceCd ?? null, itemCdHeader: t("FP_RESOURCE_CD"),
      itemNm: target.resourceNm ?? null, itemNmHeader: t("FP_RESOURCE_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. old updateChartFromGrid와 동일 — 상위 레벨을 클릭하면
  // 하위 코드는 비워서 보낸다(백엔드가 useGrouping:true일 때 이 레벨로 집계, GroupingLevel 참고).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = { plantCd: target.plantCd ?? "", resourceCd: target.resourceCd ?? "" };
    const prev = prevChartParamsRef.current;
    if (prev && prev.plantCd === nextParams.plantCd && prev.resourceCd === nextParams.resourceCd) {
      return;
    }
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    // TODO(MIG-T2): [jobchangetimeinfo/JobChangeTimeInfo] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: plantCd/resourceCd/analysisIndicator에 RealGrid autoFilter, 필터 선택값을 차트 조회의
    //        changes(FilterParameter)로 함께 전송
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다. 백엔드는 changes를 계속 받으므로 2차에 트리거 UI만
    //        얹으면 된다(가이드 §A-6e와 동일한 결정) — 지금은 빈 필터로 보낸다.
    fpJobChangeTimeInfoService.getJobChangeTimeInfoChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = data ?? [];

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartLabels([]);
          return;
        }

        setChartLabels(chartHeader);
        // old makeChartDataset — 지표(FP_TIME_MINUTES/TIMES)별로 한 행.
        setChartData(rows.map(r => {
          const point = { analysisIndicator: r.analysisIndicator };
          chartHeader.forEach((header, index) => { point[header] = r.pivotData?.[index] ?? 0; });
          return point;
        }));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveJobChangeTimeInfo = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd };

    gridRef.current?.api?.setGridOption("loading", true);

    fpJobChangeTimeInfoService.getJobChangeTimeInfos(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 선택 셀도 그 행으로 옮긴다.
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith('plant'|'resource')` 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것).
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    return { plantCd: row.plantCd, plantNm: row.plantNm, resourceCd: row.resourceCd, resourceNm: row.resourceNm };
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — 다른 Report 화면과 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/버킷)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  // old JobChangeTimeInfoChart.jsx는 ChartLegendPlugin을 plugins 배열에 실제로 포함시키지
  // 않았다(`plugins={[ChartAxisTitlePlugin]}`, PlanResult와 같은 케이스 — §A-7 "판단 시 주의"
  // 참고, old 소스의 plugins 배열을 직접 확인함). → recharts 기본 <Legend/>를 PlanResult와
  // 동일하게 화면 스코프 CSS로 숨긴다(공용 파일 수정 없이 old와 동일한 결과).
  //
  // old ChartAxisTitlePlugin의 좌우 y축 타이틀("(시간: 분)" / "(횟수)")은 PivotChartComposed의
  // series.groupName 기반 네이티브 YAxis label로 그대로 재현된다(별도 플러그인 불필요, Tier2 아님).
  const leftAxisLabel = useMemo(() => `(${t("TIME")}: ${t("FP_MINUTES")})`, [t]);
  const rightAxisLabel = useMemo(() => `(${t("TIMES")})`, [t]);

  const pivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: INDICATOR_ORDER.map(indicator => {
      const conf = INDICATOR_CONFIG[indicator];
      const isLine = conf.type === "line";
      return {
        field: indicator, text: indicator, type: conf.type,
        groupName: isLine ? rightAxisLabel : leftAxisLabel,
        orientation: conf.orientation,
        typeOptions: isLine ? { stroke: conf.color, name: t(indicator) } : { fill: conf.color, name: t(indicator) }
      };
    }),
    include: chartLabels,
    // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
    // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartLabels, leftAxisLabel, rightAxisLabel, t]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // 이 화면의 백엔드는 시간 버킷이면 빈 결과를 반환한다(JobChangeTimeInfoService의
          // isHourBucket() 조기 반환) — ResProdPlan/ResrcUtilization과 같은 이유로 WITHOUT_HOUR
          // (fp-report-screen-guide.md §A-3 배정표에 이 화면을 추가함).
          bucketCase={BUCKET_CASE.WITHOUT_HOUR}
        />
        {/* 조회 버튼 없음(§A-9b, Report 화면 공통 정책) — 자유 텍스트(resourceCdNm)는 필드 안의
            Enter/검색아이콘으로 수동 커밋. */}
        <SearchOptsGroupRetrieve
          fieldRefs={{ resourceCdNm: resourceCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveJobChangeTimeInfo}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old VerticalGridChartBox의 sizes={[50, 50]}(direction="vertical") 대응 — SplitterContainer의
          기본 initialSizes([3, 2], 60/40)를 그대로 두면 그리드:차트가 1:1이 아니게 된다(§A-1c). */}
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-job-change-time-info-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", ".recharts-legend-wrapper": { display: "none" } }}>
            {/* TODO(MIG-T2): [jobchangetimeinfo/JobChangeTimeInfo] 차트 장식성 인터랙션 미이관.
                  old: highlightChartByLabel/resetChartHighlighting(범례 hover dim) — plugins 배열에는
                       실제로 없어서 old에도 렌더링되지 않던 기능
                  사유: recharts/PivotChartComposed에 대응 훅이 없고, 1차 목표(조회·표시)에 불필요 —
                       가이드 §A-7에서 Report 카테고리 공통 Tier2 확정 항목과 동일 계열 */}
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(JobChangeTimeInfo);
